import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Button,
  Input,
  Popconfirm,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import Link from "antd/es/typography/Link";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import APIConst from "../../network/apiconst";
import axiosClient from "../../network/restclient";

export default function ListPage() {
  const { Text } = Typography;

  const navigate = useNavigate();

  const [list, setList] = useState([]);
  const [totalResult, setTotalResult] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams({
    keyword: "",
    limit: 5,
    page: 1,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get(APIConst.USER_LIST, {
        params: {
          keyword: searchParams.get("keyword"),
          limit: searchParams.get("limit"),
          page: searchParams.get("page") - 1,
        },
      });
      if (res && res.status === 0) {
        setList(res.data.list ?? []);
        setTotalResult(res.data.total ?? 0);
      } else {
        message.error(res.message ?? "Không lấy được danh sách user");
      }
    } catch (error) {
      message.error(error.message);
      console.log(error);
    }
    setLoading(false);
  };

  const deleteRecord = async (id) => {
    setLoading(true);
    try {
      const res = await axiosClient.get(APIConst.USER_DELETE, {
        params: {
          userId: id,
        },
      });
      if (res && res.status === 0) {
        fetchData();
      } else {
        message.error(res.message ?? "Không xóa được user");
      }
    } catch (error) {
      message.error(error.message);
      console.log(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    return () => {};
  }, [
    searchParams.get("refresh"),
    searchParams.get("page"),
    searchParams.get("limit"),
  ]);

  return (
    <div className="bg-body p-3 h-100 rounded">
      <div className="d-flex flex-row py-2 justify-content-between align-items-center">
        <div>
          <Input
            placeholder="Tìm kiếm tên, SĐT..."
            enterbutton="Tìm kiếm"
            name="search"
            value={searchParams.get("keyword")}
            allowClear={true}
            size="middle"
            onChange={(e) => {
              searchParams.set("keyword", e.target.value);
              setSearchParams(searchParams);
            }}
            style={{
              width: 300,
            }}
          />
          <Button
            type="primary"
            size="middle"
            onClick={(e) => {
              searchParams.set("refresh", Math.random());
              setSearchParams(searchParams);
            }}
            icon={<SearchOutlined />}
            className="ms-2"
          >
            Tra cứu
          </Button>
        </div>
        <div>
          <Button
            type="primary"
            size="middle"
            icon={<PlusOutlined />}
            onClick={() => {
              navigate("/user/create");
            }}
          >
            Tạo mới
          </Button>
        </div>
      </div>
      <div className="mt-3">
        <Table
          rowKey="_id"
          dataSource={list || []}
          loading={loading}
          bordered={true}
          size="small"
          scroll={{ x: "max-content" }}
          responsive={true}
          columns={[
            {
              title: "ID",
              dataIndex: "userId",
              key: "userId",
              align: "left",
              width: 60,
              render: (text) => <Text>{text}</Text>,
            },
            {
              title: "Tài khoản",
              dataIndex: "username",
              key: "username",
              align: "center",
              render: (text) => <Text>{text}</Text>,
            },
            {
              title: "Tên hiển thị",
              dataIndex: "fullName",
              key: "fullName",
              align: "left",
              render: (text) => <Text>{text}</Text>,
            },
            {
              title: "Ngày tạo",
              dataIndex: "createdAt",
              key: "createdAt",
              align: "center",
              width: 120,
              render: (text) => (
                <Text>
                  {dayjs(text, "DD/MM/YYYY HH:mm:ss").format("HH:mm DD/MM/YY")}
                </Text>
              ),
            },
            {
              title: "Ngày cập nhật",
              dataIndex: "updatedAt",
              key: "updatedAt",
              align: "center",
              width: 120,
              render: (text) => (
                <Text>
                  {dayjs(text, "DD/MM/YYYY HH:mm:ss").format("HH:mm DD/MM/YY")}
                </Text>
              ),
            },
            {
              title: "Trạng thái",
              dataIndex: "status",
              key: "status",
              align: "center",
              width: 80,
              render: (text) => (
                <Tag color={text === 0 ? "blue" : "magenta"} bordered={false}>
                  {text === 0 ? "Hoạt động" : "Khóa"}
                </Tag>
              ),
            },

            {
              title: "",
              dataIndex: "action",
              key: "action",
              fixed: "right",
              align: "center",
              width: 160,
              render: (text, item) => (
                <div>
                  <Link href={`/user/detail/${item?.userId}`}>
                    <EditOutlined size="small" className="me-2" />
                    Sửa
                  </Link>
                  <Popconfirm
                    title="Bạn có chắc chắn muốn xóa?"
                    onConfirm={() => {
                      deleteRecord(item?.userId);
                    }}
                    okText="Có"
                    cancelText="Không, bỏ qua"
                  >
                    <Button
                      className="ms-2"
                      type="link"
                      danger
                      icon={<DeleteOutlined size="small" />}
                    >
                      Xóa
                    </Button>
                  </Popconfirm>
                </div>
              ),
            },
          ]}
          pagination={{
            onChange: (page, pageSize) => {
              searchParams.set("page", page);
              searchParams.set("limit", pageSize);
              setSearchParams(searchParams);
            },
            current: searchParams.get("page"),
            pageSize: searchParams.get("limit"),
            total: list.length,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50"],
            defaultPageSize: 10,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} / ${total} kết quả`,
          }}
        />
      </div>
    </div>
  );
}
